import { StandardEvents } from '@shopify/events';

/**
 * Visual and pack adapter around Horizon. All commercial submission stays
 * in product-form-component; this module performs no cart/checkout requests.
 * State is scoped by section ID and survives Horizon section morphs.
 */
const sectionStates = new Map();

class MteProductComponent extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const supply = this.querySelector('[data-mte-supply]');
    const requestedPack = new URL(window.location.href).searchParams.get('pack');
    const initialPack = !window.Shopify?.designMode && ['1', '2', '3'].includes(requestedPack) && supply?.querySelector(`[data-tier="${requestedPack}"]`) ? requestedPack : null;
    this.state = sectionStates.get(this.dataset.sectionId) ?? {
      tier: initialPack ?? supply?.dataset.defaultTier ?? '3',
      flavor: this.dataset.defaultFlavor ?? 'mo',
      pending: false,
      pendingAdd: false,
      pendingDiscount: false,
      retryReceipt: null,
      galleryIndex: 0,
      defaultInitialized: false,
      productId: this.dataset.productId,
      defaultFlavor: this.dataset.defaultFlavor,
      defaultTier: supply?.dataset.defaultTier,
    };
    if (this.state.productId !== this.dataset.productId) {
      this.state.defaultInitialized = false;
      this.state.productId = this.dataset.productId;
      this.state.retryReceipt = null;
    }
    this.defaultFlavorChangedInEditor = Boolean(window.Shopify?.designMode && this.state.defaultFlavor !== this.dataset.defaultFlavor);
    if (this.state.defaultFlavor !== this.dataset.defaultFlavor) {
      this.state.defaultInitialized = false;
      this.state.defaultFlavor = this.dataset.defaultFlavor;
    }
    if (this.state.defaultTier !== supply?.dataset.defaultTier) {
      this.state.tier = supply?.dataset.defaultTier ?? '3';
      this.state.defaultTier = supply?.dataset.defaultTier;
    }
    sectionStates.set(this.dataset.sectionId, this.state);

    this.addEventListener('dear-v:pack-change', (event) => {
      if (event.target !== this || event.detail?.sectionId !== this.dataset.sectionId) return;
      const sticky = this.closest('main[data-dv-art-direction]')?.querySelector('[data-dear-v-sticky][data-pack-summary]');
      if (!sticky) return;
      const title = sticky.querySelector('[data-dear-v-sticky-title]');
      const proof = sticky.querySelector('[data-dear-v-sticky-proof]');
      if (this.dataset.dvR178 === 'true') {
        if (title) title.textContent = this.dataset.dvJustOne === 'true' ? 'Dear V · Just One' : event.detail.title;
        if (proof) {
          const star = this.querySelector('.mte-rating-pill .dv-svg-icon--star')?.cloneNode(true);
          const daily = event.detail.daily.replace(/\s*\/\s*día/, '');
          proof.replaceChildren(...(star ? [star] : []), document.createTextNode('4.9 · Sale a ' + daily + '/día (' + event.detail.total + ')'));
        }
        sticky.dataset.activePack = event.detail.tier;
        return;
      }
      if (this.dataset.dvJustOne === 'true') {
        if (title) title.textContent = 'Dear V · Just One';
        if (proof) proof.textContent = '★ 4.9 · Sale a ' + event.detail.daily.replace(' / día', '') + '/día (' + event.detail.total + ')';
        sticky.dataset.activePack = event.detail.tier;
        return;
      }
      if (title) title.textContent = event.detail.title;
      if (proof) proof.textContent = [event.detail.total, event.detail.daily, event.detail.shipping].filter(Boolean).join(' · ');
      sticky.dataset.activePack = event.detail.tier;
    }, { signal });

    this.addEventListener('dear-v:select-pack', (event) => {
      const pack = event.detail?.pack;
      if (!['1', '2', '3'].includes(pack) || !supply?.querySelector(`[data-tier="${pack}"]`)) return;
      this.state.retryReceipt = null;
      this.state.tier = pack;
      this.applyTier();
    }, { signal });

    this.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (event.target.closest('[data-mte-supply]')) {
        this.state.retryReceipt = null;
        this.state.tier = event.target.value;
        this.applyTier();
      }
    }, { signal });
    this.addEventListener('keydown', (event) => {
      const card = event.target instanceof Element ? event.target.closest('[data-pack-card][role="radio"]') : null;
      if (!card || !this.contains(card) || this.state.pendingAdd || this.state.pendingDiscount) return;
      const cards = [...this.querySelectorAll('[data-pack-card][role="radio"]')];
      const index = cards.indexOf(card);
      let next = card;
      if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = cards[(index + 1) % cards.length];
      else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = cards[(index + cards.length - 1) % cards.length];
      else if (event.key === 'Home') next = cards[0];
      else if (event.key === 'End') next = cards.at(-1);
      else if (!['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      this.state.retryReceipt = null;
      this.state.tier = next.dataset.tier;
      this.applyTier();
      next.focus({ preventScroll: true });
    }, { signal });
    this.addEventListener('click', (event) => {
      const card = event.target instanceof Element ? event.target.closest('[data-pack-card][role="radio"]') : null;
      if (card && this.contains(card)) {
        if (this.state.pendingAdd || this.state.pendingDiscount) return;
        this.state.retryReceipt = null;
        this.state.tier = card.dataset.tier;
        this.applyTier();
      }
      const target = event.target instanceof Element ? event.target.closest('[data-mte-reference-flavor]') : null;
      if (target) {
        this.state.flavor = target.dataset.mteReferenceFlavor;
        this.showGallery(this.state.flavor);
      }
    }, { signal });
    this.addEventListener(StandardEvents.productSelect, (event) => {
      this.state.retryReceipt = null;
      this.state.pending = true;
      this.applyTier();
      event.promise?.then(() => {
        this.state.pending = false;
        requestAnimationFrame(() => {
          const current = document.getElementById(this.id);
          current?.syncVariantGallery();
          current?.applyTier();
        });
      }).catch(() => {
        this.state.pending = false;
        const current = document.getElementById(this.id);
        current?.applyTier();
      });
    }, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      const ownAdd = event.action === 'add' && event.target instanceof Element && this.contains(event.target);
      const receipt = ownAdd ? this.purchaseReceipt() : null;
      if (ownAdd) {
        this.state.pendingAdd = true;
        this.applyTier();
      }
      event.promise?.then(({ detail }) => {
        if (ownAdd) this.state.pendingAdd = false;
        const current = document.getElementById(this.id);
        current?.applyTier();
        if (ownAdd && detail?.didError === false && current) {
          receipt.cartItems = detail.items;
          current.completePurchase(receipt);
        }
      }).catch(() => {
        if (ownAdd) this.state.pendingAdd = false;
        const current = document.getElementById(this.id);
        current?.applyTier();
      });
    }, { signal });
    this.addEventListener('submit', (event) => {
      if (!this.isReady()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
    }, { capture: true, signal });

    this.querySelector('[data-mte-prev]')?.addEventListener('click', () => this.goToMedia(this.state.galleryIndex - 1), { signal });
    this.querySelector('[data-mte-next]')?.addEventListener('click', () => this.goToMedia(this.state.galleryIndex + 1), { signal });
    this.syncVariantGallery();
    Promise.all([
      customElements.whenDefined('product-form-component'),
      customElements.whenDefined('quantity-selector-component'),
      customElements.whenDefined('variant-picker'),
    ]).then(() => {
      if (!this.isConnected) return;
      this.applyTier();
      this.initializeDefaultFlavor();
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.galleryAbort?.abort();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  selectedTier() {
    return Array.from(this.querySelectorAll('[data-tier]')).find((tier) => tier.dataset.tier === this.state.tier);
  }

  usesVariantPacks() {
    return this.querySelector('[data-mte-supply]')?.dataset.dvR17 === 'true';
  }

  isReady() {
    const tier = this.selectedTier();
    if (!tier || this.state.pending || this.state.pendingAdd || this.state.pendingDiscount || this.dataset.commerceEnabled !== 'true' || this.dataset.hasProduct !== 'true' || this.dataset.variantAvailable !== 'true') return false;
    if (this.usesVariantPacks() && (!tier.dataset.variantId || tier.dataset.variantAvailable !== 'true' || Number(tier.dataset.productPrice) !== Number(tier.dataset.expectedPrice))) return false;
    if (this.state.retryReceipt) return true;
    const quantity = this.querySelector('quantity-selector-component');
    const input = quantity?.querySelector('input[name="quantity"]');
    const amount = Number(tier.dataset.quantity);
    if (!input || !Number.isInteger(amount) || amount <= 0) return false;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const step = Number(input.step) || 1;
    if (amount < min || amount > max || (amount - min) % step !== 0) return false;
    if (quantity.canAddToCart && !quantity.canAddToCart().canAdd) return false;
    if (tier.dataset.giftRequired === 'true') {
      const supply = this.querySelector('[data-mte-supply]');
      if (supply?.dataset.giftsReady !== 'true') return false;
      if (Array.from(tier.querySelectorAll('[data-destination-gift-id]')).some((gift) => !gift.dataset.destinationGiftId)) return false;
      if (supply.dataset.giftMode === 'bundle' && Array.from(tier.querySelectorAll('[data-destination-gift-variant-id]')).some((gift) => !gift.dataset.destinationGiftVariantId || gift.dataset.giftAvailable !== 'true' || Number(gift.dataset.giftMin) > 1 || gift.dataset.giftRequiresPlan === 'true')) return false;
    }
    return true;
  }

  applyTier() {
    const selected = this.selectedTier();
    if (!selected) return;
    const variantPacks = this.usesVariantPacks();
    this.querySelectorAll('[data-tier]').forEach((tier) => {
      const active = tier === selected;
      tier.classList.toggle('is-selected', active);
      const input = tier.querySelector('input[type="radio"]');
      if (input) input.checked = active;
      if (input) input.disabled = this.state.pendingAdd || this.state.pendingDiscount;
      if (tier.getAttribute('role') === 'radio') {
        tier.setAttribute('aria-checked', String(active));
        tier.tabIndex = active ? 0 : -1;
        tier.setAttribute('aria-disabled', String(this.state.pendingAdd || this.state.pendingDiscount));
      }
      tier.querySelector('.dv-pack-card__drawer')?.setAttribute('aria-hidden', String(!active));
    });
    if (variantPacks) {
      const variant = this.querySelector('product-form-component input[name="id"]');
      if (variant) variant.value = selected.dataset.variantId || '';
      this.dataset.variantAvailable = selected.dataset.variantAvailable;
    }
    const quantity = this.querySelector('quantity-selector-component');
    quantity?.setValue?.(selected.dataset.quantity);
    this.querySelectorAll('variant-picker input, variant-picker select').forEach((input) => {
      input.disabled = this.state.pendingAdd || this.state.pendingDiscount;
    });
    const form = this.querySelector('product-form-component');
    if (form) {
      form.submissionAdapter = () => {
        if (!this.isReady()) return false;
        if (this.state.retryReceipt) {
          this.completePurchase(this.state.retryReceipt);
          return false;
        }
        const tier = this.selectedTier();
        const supply = this.querySelector('[data-mte-supply]');
        if (tier?.dataset.giftRequired !== 'true' || supply?.dataset.giftMode !== 'bundle') return null;
        return Array.from(tier.querySelectorAll('[data-destination-gift-variant-id]')).map((gift) => ({
          variantId: gift.dataset.destinationGiftVariantId,
          quantity: 1,
        }));
      };
    }
    const button = this.querySelector('product-form-component [ref="addToCartButton"]');
    if (button) {
      if (this.dataset.isolatedFunnel === 'true') button.classList.add('mte-buy-button');
      button.disabled = !this.isReady();
      button.setAttribute('aria-disabled', String(button.disabled));
      let copy = button.querySelector('[data-mte-cta-copy]');
      if (!copy) {
        copy = document.createElement('span');
        copy.dataset.mteCtaCopy = '';
        Array.from(button.childNodes).filter((node) => node.nodeType === Node.TEXT_NODE).forEach((node) => node.remove());
        button.prepend(copy);
      }
      if (this.dataset.dvR179 === 'true') {
        button.dataset.dvCtaBtn = '';
        const text = document.createElement('span');
        text.className = 'mte-buy-button__main';
        text.dataset.dvCtaLabel = '';
        text.textContent = `AÑADIR AL CARRITO · ${selected.dataset.packTotal}`;
        const badge = document.createElement('span');
        badge.className = 'mte-buy-button__sub';
        badge.dataset.dvCtaBadge = '';
        const qty = selected.dataset.packQty;
        const save = selected.dataset.packSave;
        if (qty === '3') badge.textContent = `Envío Express Gratis + Ahorras ${save}`;
        else if (qty === '2') badge.textContent = `Envío Gratis + Ahorras ${save}`;
        else badge.textContent = 'Pack 1 Mes (30 días) · Garantía 60 días';
        copy.replaceChildren(text, badge);
      } else if (this.dataset.dvR178 === 'true') {
        button.dataset.dvCtaBtn = '';
        const text = document.createElement('span');
        text.className = 'mte-buy-button__text';
        text.dataset.dvCtaLabel = '';
        text.textContent = 'Empezar mi ritual · ' + selected.dataset.packTotal;
        const badge = document.createElement('span');
        badge.className = 'mte-buy-button__Subbadge';
        badge.dataset.dvCtaBadge = '';
        badge.textContent = selected.dataset.packSave ? 'Ahorra ' + selected.dataset.packSave + ' →' : (this.dataset.dvJustOne === 'true' ? '→' : 'Añadir al carrito →');
        copy.replaceChildren(text, badge);
      } else copy.textContent = this.dataset.dvRitualLabel || (variantPacks ? `Añadir ${selected.dataset.packTitle} (${selected.dataset.packTotal}) →` : this.dataset.ctaText);
    }
    if (this.dataset.isolatedFunnel === 'true' && selected.dataset.packTitle) {
      this.dispatchEvent(new CustomEvent('dear-v:pack-change', {
        bubbles: true,
        detail: {
          sectionId: this.dataset.sectionId,
          tier: this.state.tier,
          title: selected.dataset.packTitle,
          total: selected.dataset.packTotal,
          daily: selected.dataset.packDaily,
          shipping: selected.dataset.packShipping,
          ready: this.isReady(),
        },
      }));
    }
  }

  syncVariantGallery() {
    this.querySelectorAll('variant-picker input[type="radio"]').forEach((input) => {
      const gallery = Array.from(this.querySelectorAll('[data-mte-gallery][data-option-value]')).find((item) => item.dataset.optionValue === input.value);
      const label = input.closest('label');
      if (gallery && label) {label.dataset.mteFlavor = gallery.dataset.mteGallery;label.style.setProperty('--mte-flavor-chip',gallery.dataset.flavorChip);}
    });
    const checked = Array.from(this.querySelectorAll('variant-picker input:checked, variant-picker select')).map((input) => input.value);
    const matched = Array.from(this.querySelectorAll('[data-mte-gallery][data-option-value]')).find((gallery) => checked.includes(gallery.dataset.optionValue));
    this.showGallery(matched?.dataset.mteGallery ?? this.state.flavor);
  }

  purchaseReceipt() {
    const tier = this.selectedTier();
    const main = this.querySelector('product-form-component input[name="id"]');
    const items = [{ variantId: main?.value, quantity: Number(tier?.dataset.quantity) }];
    const supply = this.querySelector('[data-mte-supply]');
    if (tier?.dataset.giftRequired === 'true' && supply?.dataset.giftMode === 'bundle') {
      tier.querySelectorAll('[data-destination-gift-variant-id]').forEach((gift) => items.push({
        variantId: gift.dataset.destinationGiftVariantId, quantity: 1,
      }));
    }
    return {
      tier: this.state.tier,
      codes: [tier?.dataset.discountCode, tier?.dataset.giftDiscountCode].filter(Boolean),
      action: this.dataset.postAddAction,
      replaceOffer: this.dataset.replaceOffer === 'true',
      items,
    };
  }

  offerUpdates(items, cartItems) {
    if (!Array.isArray(cartItems)) throw new Error('Native cart lines unavailable');
    const quantities = new Map();
    for (const item of items) {
      const id = String(item.variantId);
      quantities.set(id, (quantities.get(id) ?? 0) + item.quantity);
    }
    const updates = {};
    for (const line of cartItems) {
      const id = String(line.variant_id ?? line.id);
      if (!line.key) throw new Error('Native cart line key unavailable');
      updates[line.key] = quantities.get(id) ?? 0;
      quantities.delete(id);
    }
    if (quantities.size) throw new Error('Selected offer missing from native cart');
    return updates;
  }

  validateOffer(receipt, cart) {
    if (!receipt.replaceOffer) return;
    const expected = new Map();
    for (const item of receipt.items) {
      const id = String(item.variantId);
      expected.set(id, (expected.get(id) ?? 0) + item.quantity);
    }
    const actual = new Map();
    for (const line of cart.items ?? []) {
      const id = String(line.variant_id ?? line.id);
      actual.set(id, (actual.get(id) ?? 0) + line.quantity);
    }
    if (actual.size !== expected.size || Array.from(expected).some(([id, amount]) => actual.get(id) !== amount)) {
      throw new Error('Selected offer quantities were not confirmed');
    }
  }

  async completePurchase(receipt) {
    if (!receipt || this.state.pendingDiscount) return;
    this.state.pendingDiscount = true;
    this.applyTier();
    try {
      if (receipt.replaceOffer && !receipt.updates) receipt.updates = this.offerUpdates(receipt.items, receipt.cartItems);
      if (receipt.codes.length || receipt.replaceOffer) {
        await customElements.whenDefined('cart-discount-component');
        const discount = this.querySelector('[data-mte-discount-helper]');
        if (!discount?.applyCodes) throw new Error('Native discount component unavailable');
        const cart = await discount.applyCodes(receipt.codes, { updates: receipt.updates });
        this.validateOffer(receipt, cart);
      }
      this.state.retryReceipt = null;
      if (this.dataset.isolatedFunnel === 'true' || receipt.action === 'drawer') {
        await customElements.whenDefined('theme-drawer');
        document.getElementById('cart-drawer')?.open();
        return;
      }
      const destination = receipt.action === 'cart' ? this.dataset.cartUrl : this.dataset.checkoutUrl;
      if (destination) location.assign(destination);
    } catch (error) {
      this.state.retryReceipt = receipt;
      const message = this.dataset.discountError || error.message;
      const surface = this.querySelector('product-form-component [ref="addToCartTextError"]');
      if (surface) {
        surface.classList.remove('hidden');
        surface.setAttribute('role', 'alert');
        let copy = surface.querySelector('[data-mte-discount-error]');
        if (!copy) {
          copy = document.createElement('span');
          copy.dataset.mteDiscountError = '';
          surface.append(copy);
        }
        copy.textContent = message;
      }
      const live = this.querySelector('product-form-component [ref="liveRegion"]');
      if (live) live.textContent = message;
    } finally {
      this.state.pendingDiscount = false;
      if (this.isConnected) this.applyTier();
    }
  }

  initializeDefaultFlavor() {
    const picker = this.querySelector('variant-picker');
    if (!picker || this.state.defaultInitialized) return;
    this.state.defaultInitialized = true;
    if (new URL(location.href).searchParams.has('variant') && !this.defaultFlavorChangedInEditor) return;
    const gallery = Array.from(this.querySelectorAll('[data-mte-gallery]')).find((item) => item.dataset.mteGallery === this.dataset.defaultFlavor);
    const option = Array.from(picker.querySelectorAll('input[type="radio"], select option')).find((input) => input.value === gallery?.dataset.optionValue);
    if (!option || option.getAttribute('aria-disabled') === 'true' || option.disabled) return;
    if (option instanceof HTMLOptionElement) {
      const select = option.closest('select');
      if (!select || select.value === option.value) return;
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      if (option.checked) return;
      option.checked = true;
      option.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  showGallery(flavor) {
    this.galleryAbort?.abort();
    this.galleryAbort = new AbortController();
    const { signal } = this.galleryAbort;
    this.state.flavor = flavor;
    this.querySelectorAll('[data-mte-reference-flavor]').forEach((button) => {
      const active = button.dataset.mteReferenceFlavor === flavor;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-pressed', String(active));
    });
    const galleries = Array.from(this.querySelectorAll('[data-mte-gallery]'));
    const active = galleries.find((gallery) => gallery.dataset.mteGallery === 'product') ?? galleries.find((gallery) => gallery.dataset.mteGallery === flavor) ?? galleries[0];
    galleries.forEach((gallery) => {
      gallery.hidden = gallery !== active;
      gallery.querySelectorAll('video').forEach((video) => {
        if (gallery !== active) video.pause();
        else if (video.autoplay && !matchMedia('(prefers-reduced-motion: reduce)').matches) video.play().catch(() => {});
      });
    });
    if (!active) return;
    this.activeGallery = active;
    this.state.galleryIndex = 0;
    const thumbs = this.querySelector('[data-mte-thumbs]');
    thumbs?.replaceChildren();
    const tiles = Array.from(active.querySelectorAll('.image-tile'));
    if (this.dataset.isolatedFunnel === 'true') {
      active.tabIndex = 0;
      active.addEventListener('keydown', (event) => {
        if (event.target !== active) return;
        let target = null;
        if (event.key === 'ArrowRight') target = this.state.galleryIndex + 1;
        if (event.key === 'ArrowLeft') target = this.state.galleryIndex - 1;
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = tiles.length - 1;
        if (target === null) return;
        event.preventDefault();
        this.goToMedia(target);
      }, { signal });
    }
    tiles.forEach((tile, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mobile-carousel-thumb dv-thumb-item';
      const slideTitle = tile.dataset.dvTitle || tile.querySelector('[data-dv-title]')?.dataset.dvTitle || '';
      if (slideTitle) button.dataset.dvTitle = slideTitle;
      button.setAttribute('aria-label', slideTitle ? `${slideTitle} (${index + 1} de ${tiles.length})` : (tile.querySelector('video') ? this.dataset.thumbVideoLabel : this.dataset.thumbImageLabel) + ' ' + (index + 1));
      const image = tile.querySelector('img');
      if (image) {
        const thumbnail = document.createElement('img');
        thumbnail.src = image.currentSrc || image.src;
        thumbnail.alt = '';
        thumbnail.loading = 'lazy';
        button.append(thumbnail);
      } else {
        const mark = document.createElement('span');
        mark.className = 'thumb-video-mark';
        mark.setAttribute('aria-hidden', 'true');
        mark.textContent = '▶';
        button.append(mark);
      }
      button.addEventListener('click', () => this.goToMedia(index), { signal });
      thumbs?.append(button);
    });
    this.querySelector('[data-dv-stage-prev]')?.addEventListener('click', () => this.goToMedia((this.state.galleryIndex - 1 + tiles.length) % tiles.length), { signal });
    this.querySelector('[data-dv-stage-next]')?.addEventListener('click', () => this.goToMedia((this.state.galleryIndex + 1) % tiles.length), { signal });
    active.scrollLeft = 0;
    active.addEventListener('scroll', () => {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const width = tiles[0]?.getBoundingClientRect().width;
        if (width) {
          this.state.galleryIndex = Math.max(0, Math.min(tiles.length - 1, Math.round(active.scrollLeft / width)));
          this.updateGalleryControls();
        }
      });
    }, { passive: true, signal });
    this.updateGalleryControls();
  }

  goToMedia(index) {
    const tiles = Array.from(this.activeGallery?.querySelectorAll('.image-tile') ?? []);
    const target = Math.max(0, Math.min(tiles.length - 1, index));
    this.state.galleryIndex = target;
    this.activeGallery?.scrollTo({
      left: tiles[target]?.offsetLeft - this.activeGallery.offsetLeft,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
    this.updateGalleryControls();
  }

  updateGalleryControls() {
    const tiles = Array.from(this.activeGallery?.querySelectorAll('.image-tile') ?? []);
    const index = this.state.galleryIndex;
    tiles.forEach((tile, i) => tile.classList.toggle('is-active', i === index));
    this.querySelectorAll('.mobile-carousel-thumb').forEach((button, i) => {
      button.classList.toggle('is-active', i === index);
      button.setAttribute('aria-pressed', String(i === index));
    });
    const counterText = this.querySelector('[data-dv-gallery-counter-text]') || this.querySelector('[data-dv-gallery-counter]');
    if (counterText && tiles.length) counterText.textContent = `${index + 1} / ${tiles.length}`;
    const progress = this.querySelector('[data-dv-gallery-progress]');
    if (progress && tiles.length) progress.style.width = `${((index + 1) / tiles.length) * 100}%`;
    const previous = this.querySelector('[data-mte-prev]');
    const next = this.querySelector('[data-mte-next]');
    if (previous) previous.disabled = !tiles.length || index === 0;
    if (next) next.disabled = !tiles.length || index >= tiles.length - 1;
  }
}

if (!customElements.get('mte-product-component')) {
  customElements.define('mte-product-component', MteProductComponent);
}

